import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { store } from "../stores/Store";
import { formatBRL } from "../utils";
import type { OrderDetails as OrderDetailsType } from "../types/store";

export const descricaoByState: Record<string, string> = {
  CRIADO: "Pedido criado",
  AGUARDANDO_PAGAMENTO: "Aguardando confirmação de pagamento",
  PREPARANDO_PACOTE: "Preparando pacote para envio",
  PEDIDO_ENVIADO: "Pedido enviado para entrega",
  ENTREGUE: "Pedido entregue ao destinatário",
};

export const OrderDetails: React.FC = () => {
  const location = useLocation();
  const { orderId, email, pixCopiaECola, pixValor } = location.state || {};
  const [order, setOrder] = useState<OrderDetailsType | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pixCode, setPixCode] = useState<string | null>(pixCopiaECola || null);
  const [pixAmount, setPixAmount] = useState<number | null>(pixValor || null);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId || !email) return;
      setLoading(true);
      try {
        const data = await store.checkOrder(orderId, email);
        setOrder(data);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId, email]);

  if (loading) {
    return (
      <div
        className="col-md-8 col-md-push-2 text-center"
        style={{ padding: "100px 0" }}
      >
        <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
          Carregando pedidos.
        </h2>
      </div>
    );
  }

  if (!order) {
    return (
      <div
        className="col-md-8 col-md-push-2 text-center"
        style={{ padding: "100px 0" }}
      >
        <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
          Pedido não encontrado.
        </h2>
      </div>
    );
  }

  return (
    <div className="container-fluid bg_branco">
      <div className="container ctn_default">
        <div className="row">
          <div className="col-md-8 col-md-push-2">
            <h1 className="titulo titulo_25 titulo_400 txt_preto mg-bt_40">
              <span className="material-symbols-outlined">receipt_long</span>
              Pedido N° {order.id}
            </h1>

            {pixCode && (
              <div className="mg-bt_40" style={{ textAlign: "center" }}>
                <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
                  <span className="material-symbols-outlined">pix</span>
                  Pagamento PIX
                </h2>
                <p className="titulo_15 mg-bt_20">
                  Valor: <b>{formatBRL(pixAmount ?? 0)}</b>
                </p>
                <div className="mg-bt_20">
                  <QRCodeSVG value={pixCode} size={250} />
                </div>
                <p className="titulo_15 mg-bt_10">
                  Ou copie o código PIX abaixo:
                </p>
                <div className="mg-bt_20" style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                  <input
                    type="text"
                    readOnly
                    value={pixCode}
                    style={{ flex: 1, maxWidth: 400, fontSize: 12 }}
                  />
                  <button
                    type="button"
                    className="bt bt_cor-id-3"
                    onClick={() => {
                      navigator.clipboard.writeText(pixCode);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? "Copiado!" : "Copiar"}
                  </button>
                </div>
                <button
                  type="button"
                  className="bt bt_big mg-bt_20"
                  disabled={gerando}
                  onClick={async () => {
                    setGerando(true);
                    const cobranca = await store.gerarCobrancaPix(orderId);
                    if (cobranca) {
                      setPixCode(cobranca.pix_copia_e_cola);
                      setPixAmount(cobranca.valor);
                    }
                    setGerando(false);
                  }}
                >
                  {gerando ? "Gerando..." : "Gerar novo QR Code"}
                </button>
              </div>
            )}

            <div className="ctn_timeline mg-bt_40">
              {order.statusHistory.map((status, idx) => (
                <div
                  key={status.status}
                  className={status.concluida ? "status_ok" : "status_waiting"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <h2 className="data titulo_15 titulo_700 txt_preto mg-bt_4">
                      {status.date
                        ? new Date(status.date).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "--/--/---- --:--"}
                    </h2>
                    <div className="description mg-bt_4">
                      <p>
                        <b>{descricaoByState[status.status]}</b>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <a href="/" className="bt bt_big">
              Voltar para Loja
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
